import React, {useContext, useState, useEffect} from 'react';
import {Button, TextField, Typography} from '@mui/material';
import {ApplicationContext} from '../context/ApplicationContext';
import {generate2FASetup, complete2FASetup} from '../services/login.service';
import {CircularProgress} from "@mui/material";
import {useLocation} from 'react-router-dom';
import config from "../config";
import './Login.scss';

const Setup2FAPage = (props) => {
    const AppContext = useContext(ApplicationContext);
    const location = useLocation();
    const [username, setUsername] = useState('');
    const [code, setCode] = useState('');
    const [isWaitingOnServer, setIsWaitingOnServer] = useState(false);
    const [isGenerating, setIsGenerating] = useState(true);
    const [qrCode, setQrCode] = useState('');
    const [secret, setSecret] = useState('');

    useEffect(() => {
        // Get username from location state or query params
        const stateUsername = location?.state?.username;
        const urlParams = new URLSearchParams(window.location.search);
        const queryUsername = urlParams.get('username');
        
        const user = stateUsername || queryUsername;
        if (!user) {
            // No username found, redirect to login
            window.location.replace(`${config.uiBaseUrl}/`);
            return;
        }
        setUsername(user);
        generateSetup(user);
    }, [location]);

    const generateSetup = async (username) => {
        setIsGenerating(true);
        try {
            const response = await generate2FASetup(username);
            setQrCode(response.qr_code);
            setSecret(response.secret);
            setIsGenerating(false);
        } catch (err) {
            console.log(err);
            AppContext.handleError('Failed to generate 2FA setup. Please try again.');
            setIsGenerating(false);
        }
    };

    const submitForm = (username, secret, code) => {
        setIsWaitingOnServer(true);
        complete2FASetup(username, secret, code)
            .then((reply) => {
                const destination_page = `${config.uiBaseUrl}/instantAnalytics`
                window.location.replace(destination_page);
                setIsWaitingOnServer(false);
            })
            .catch((err) => {
                console.log(err);
                AppContext.handleError('Invalid verification code. Please try again.');
                setIsWaitingOnServer(false);
                setCode(''); // Clear the code field
            });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && code && code.length === 6 && !isWaitingOnServer) {
            submitForm(username, secret, code);
        }
    };

    const handleCodeChange = (e) => {
        // Only allow numeric input and limit to 6 digits
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setCode(value);
    };

    if (!username) {
        return (
            <div className="login-container">
                <div className="login-card loading-state">
                    <CircularProgress size={48} style={{color: '#ffffff', marginBottom: '24px'}}/>
                    <Typography variant="h6" className="primary-font" style={{fontWeight: 500}}>
                        Loading...
                    </Typography>
                </div>
            </div>
        );
    }

    if (isGenerating) {
        return (
            <div className="login-container">
                <div className="login-card loading-state">
                    <CircularProgress size={48} style={{color: '#ffffff', marginBottom: '24px'}}/>
                    <Typography variant="h6" className="primary-font" style={{fontWeight: 500}}>
                        Generating 2FA setup...
                    </Typography>
                </div>
            </div>
        );
    }

    if (isWaitingOnServer) {
        return (
            <div className="login-container">
                <div className="login-card loading-state">
                    <CircularProgress size={48} style={{color: '#ffffff', marginBottom: '24px'}}/>
                    <Typography variant="h6" className="primary-font" style={{fontWeight: 500}}>
                        Verifying...
                    </Typography>
                </div>
            </div>
        )
    }

    return (
        <div className="login-container">
            <div className="login-bg-orbs" aria-hidden="true">
                <div className="orb orb-1" />
                <div className="orb orb-2" />
                <div className="orb orb-3" />
                <div className="orb orb-4" />
            </div>

            <div className="login-card">
                <div className="login-header">
                    <Typography
                        variant="h3"
                        className="login-title"
                        style={{fontFamily: 'Cookie, cursive', fontSize: '48px', marginBottom: '4px'}}
                    >
                        Verdantime
                    </Typography>
                    <div className="login-accent-bar"/>
                    <Typography variant="body2" className="secondary-font" style={{marginTop: '16px', marginBottom: 0}}>
                        Scan the QR code with your authenticator app, then enter the code to verify
                    </Typography>
                </div>

                <div className="login-form" style={{textAlign: 'center'}}>
                    {qrCode && (
                        <div style={{marginBottom: '32px', display: 'flex', justifyContent: 'center'}}>
                            <img
                                src={`data:image/png;base64,${qrCode}`}
                                alt="QR Code for 2FA Setup"
                                style={{
                                    width: '100%',
                                    maxWidth: '220px',
                                    height: 'auto',
                                    backgroundColor: 'white',
                                    padding: '16px',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                                }}
                            />
                        </div>
                    )}
                    
                    <Typography variant="body2" className="secondary-font" style={{opacity: 0.6, marginBottom: '24px', fontSize: '14px'}}>
                        Can't scan? Enter this code manually in your authenticator app:
                    </Typography>
                    <Typography 
                        variant="body1" 
                        className="primary-font" 
                        style={{
                            fontFamily: 'monospace',
                            fontSize: '18px',
                            letterSpacing: '2px',
                            marginBottom: '32px',
                            padding: '12px',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '8px',
                            wordBreak: 'break-all'
                        }}
                    >
                        {secret}
                    </Typography>

                    <TextField 
                        onChange={handleCodeChange}
                        onKeyDown={handleKeyDown}
                        label="Verification Code" 
                        variant="outlined"
                        fullWidth
                        className="login-input"
                        autoComplete="one-time-code"
                        autoFocus
                        value={code}
                        inputProps={{
                            maxLength: 6,
                            style: { 
                                textAlign: 'center',
                                fontSize: '24px',
                                letterSpacing: '8px',
                                fontFamily: 'monospace'
                            }
                        }}
                        placeholder="000000"
                    />

                    <Button 
                        className="login-button primary-font"
                        onClick={() => submitForm(username, secret, code)} 
                        disabled={isWaitingOnServer || !code || code.length !== 6}
                        variant="contained"
                        fullWidth
                        size="large"
                        style={{marginTop: '32px'}}
                    >
                        Complete Setup
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default Setup2FAPage;

